import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Shield, UserX, Flag, Eye, EyeOff, Lock, AlertTriangle, Phone, MessageSquare, CheckCircle2 } from 'lucide-react';
interface SafetyModalProps {
  onClose: () => void;
}
export function SafetyModal({
  onClose
}: SafetyModalProps) {
  const [incognitoMode, setIncognitoMode] = useState(false);
  const [hideAge, setHideAge] = useState(false);
  const [hideDistance, setHideDistance] = useState(false);
  const Toggle = ({
    enabled,
    onChange
  }: {
    enabled: boolean;
    onChange: (val: boolean) => void;
  }) => <button onClick={() => onChange(!enabled)} className={`w-12 h-7 rounded-full relative transition-colors ${enabled ? 'bg-gradient-to-r from-blue-500 to-indigo-500' : 'bg-zinc-200'}`} data-id="element-419">
      <motion.div animate={{
      x: enabled ? 22 : 2
    }} transition={{
      type: 'spring',
      stiffness: 500,
      damping: 30
    }} className="absolute top-1 w-5 h-5 bg-white rounded-full shadow-sm" data-id="element-420" />
    </button>;
  const safetyFeatures = [{
    icon: Shield,
    title: 'Photo Verification',
    description: 'Verify your photos to get a blue badge',
    action: 'Verify Now',
    color: 'blue'
  }, {
    icon: UserX,
    title: 'Blocked Users',
    description: 'Manage your blocked users list',
    action: '3 blocked',
    color: 'red'
  }, {
    icon: Flag,
    title: 'Report History',
    description: 'View your report submissions',
    action: 'View',
    color: 'orange'
  }];
  const emergencyContacts = [{
    icon: Phone,
    label: 'Emergency Services',
    number: '911'
  }, {
    icon: MessageSquare,
    label: 'Crisis Text Line',
    number: 'Text HOME to 741741'
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-421">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="absolute right-0 top-0 bottom-0 w-full bg-white overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-422">
        {/* Header */}
        <div className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-zinc-100 px-4 py-4 flex items-center gap-4 z-10" data-id="element-423">
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors" data-id="element-424">
            <X size={24} className="text-zinc-600" data-id="element-425" />
          </button>
          <h1 className="text-xl font-bold text-zinc-900" data-id="element-426">Safety Center</h1>
        </div>

        <div className="p-4 space-y-6 pb-8" data-id="element-427">
          {/* Safety Banner */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-4 text-white" data-id="element-428">
            <div className="flex items-center gap-3 mb-2" data-id="element-429">
              <Shield size={24} data-id="element-430" />
              <h2 className="font-bold text-lg" data-id="element-431">Your Safety Matters</h2>
            </div>
            <p className="text-white/80 text-sm" data-id="element-432">
              We're committed to keeping Spark a safe place. Use these tools to
              protect yourself.
            </p>
          </div>

          {/* Safety Features */}
          <div data-id="element-433">
            <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3 px-1" data-id="element-434">
              Safety Features
            </h2>
            <div className="space-y-3" data-id="element-435">
              {safetyFeatures.map((feature, index) => <button key={index} className="w-full flex items-center gap-4 p-4 bg-zinc-50 rounded-2xl hover:bg-zinc-100 transition-colors" data-id="element-436">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${feature.color === 'blue' ? 'bg-blue-100 text-blue-500' : feature.color === 'red' ? 'bg-red-100 text-red-500' : 'bg-orange-100 text-orange-500'}`} data-id="element-437">
                    <feature.icon size={24} data-id="element-438" />
                  </div>
                  <div className="flex-1 text-left" data-id="element-439">
                    <p className="font-semibold text-zinc-900" data-id="element-440">
                      {feature.title}
                    </p>
                    <p className="text-sm text-zinc-500" data-id="element-441">
                      {feature.description}
                    </p>
                  </div>
                  <span className="text-sm font-medium text-rose-500" data-id="element-442">
                    {feature.action}
                  </span>
                </button>)}
            </div>
          </div>

          {/* Privacy Controls */}
          <div data-id="element-443">
            <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3 px-1" data-id="element-444">
              Privacy Controls
            </h2>
            <div className="bg-zinc-50 rounded-2xl overflow-hidden" data-id="element-445">
              <div className="flex items-center justify-between p-4 border-b border-zinc-100" data-id="element-446">
                <div className="flex items-center gap-3" data-id="element-447">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm" data-id="element-448">
                    <EyeOff size={20} className="text-zinc-500" data-id="element-449" />
                  </div>
                  <div data-id="element-450">
                    <p className="font-medium text-zinc-900" data-id="element-451">Incognito Mode</p>
                    <p className="text-xs text-zinc-500" data-id="element-452">
                      Only people you like can see you
                    </p>
                  </div>
                </div>
                <Toggle enabled={incognitoMode} onChange={setIncognitoMode} data-id="element-453" />
              </div>
              <div className="flex items-center justify-between p-4 border-b border-zinc-100" data-id="element-454">
                <div className="flex items-center gap-3" data-id="element-455">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm" data-id="element-456">
                    <Lock size={20} className="text-zinc-500" data-id="element-457" />
                  </div>
                  <div data-id="element-458">
                    <p className="font-medium text-zinc-900" data-id="element-459">Hide Age</p>
                    <p className="text-xs text-zinc-500" data-id="element-460">
                      Don't show your age on profile
                    </p>
                  </div>
                </div>
                <Toggle enabled={hideAge} onChange={setHideAge} data-id="element-461" />
              </div>
              <div className="flex items-center justify-between p-4" data-id="element-462">
                <div className="flex items-center gap-3" data-id="element-463">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm" data-id="element-464">
                    <Eye size={20} className="text-zinc-500" data-id="element-465" />
                  </div>
                  <div data-id="element-466">
                    <p className="font-medium text-zinc-900" data-id="element-467">Hide Distance</p>
                    <p className="text-xs text-zinc-500" data-id="element-468">
                      Don't show how far away you are
                    </p>
                  </div>
                </div>
                <Toggle enabled={hideDistance} onChange={setHideDistance} data-id="element-469" />
              </div>
            </div>
          </div>

          {/* Safety Tips */}
          <div data-id="element-470">
            <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3 px-1" data-id="element-471">
              Safety Tips
            </h2>
            <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200" data-id="element-472">
              <div className="flex items-start gap-3" data-id="element-473">
                <AlertTriangle size={20} className="text-amber-500 mt-0.5" data-id="element-474" />
                <div data-id="element-475">
                  <p className="font-semibold text-amber-800 mb-2" data-id="element-476">
                    Stay Safe When Meeting
                  </p>
                  <ul className="space-y-2 text-sm text-amber-700" data-id="element-477">
                    <li className="flex items-center gap-2" data-id="element-478">
                      <CheckCircle2 size={14} data-id="element-479" />
                      Meet in a public place
                    </li>
                    <li className="flex items-center gap-2" data-id="element-480">
                      <CheckCircle2 size={14} data-id="element-481" />
                      Tell a friend where you're going
                    </li>
                    <li className="flex items-center gap-2" data-id="element-482">
                      <CheckCircle2 size={14} data-id="element-483" />
                      Don't share personal info too soon
                    </li>
                    <li className="flex items-center gap-2" data-id="element-484">
                      <CheckCircle2 size={14} data-id="element-485" />
                      Trust your instincts
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Emergency Contacts */}
          <div data-id="element-486">
            <h2 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3 px-1" data-id="element-487">
              Emergency Resources
            </h2>
            <div className="space-y-2" data-id="element-488">
              {emergencyContacts.map((contact, index) => <div key={index} className="flex items-center gap-4 p-4 bg-red-50 rounded-2xl border border-red-100" data-id="element-489">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center" data-id="element-490">
                    <contact.icon size={20} className="text-red-500" data-id="element-491" />
                  </div>
                  <div className="flex-1" data-id="element-492">
                    <p className="font-semibold text-red-800" data-id="element-493">
                      {contact.label}
                    </p>
                    <p className="text-sm text-red-600" data-id="element-494">{contact.number}</p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>;
}